import type {
  ModuleID,
  ModulesConfig,
  VersionString,
  ViewModuleRef,
} from "@bestowinc/tinkertoy-core";

export { isModuleRef } from "@bestowinc/tinkertoy-core";

/**
 * Runtime shape of a {@link ViewModuleRef}'s `props` after callback resolution.
 *
 * `module()` types `props` as a record (or a callback returning one); FEP2-3157
 * will further tighten registry-backed prop checking. The convention at the
 * renderer layer is that every module's resolved props are a record.
 * {@link resolveModuleProps} coerces non-objects to `{}` so all downstream
 * consumers can rely on this shape. A branch module's slots aren't props:
 * they arrive on the ref itself (`ref.slots`).
 */
export type ResolvedProps = Record<string, unknown>;

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Resolve a module ref's `props` field — invokes the callback form with the
 * view's {@link ModulesConfig}, otherwise passes through. Non-record values
 * are coerced to `{}` so consumers can rely on {@link ResolvedProps}.
 */
export function resolveModuleProps(
  ref: ViewModuleRef,
  config: ModulesConfig,
): ResolvedProps {
  const raw = typeof ref.props === "function" ? ref.props(config) : ref.props;
  return isObjectRecord(raw) ? raw : {};
}

export function getModuleVersion(
  ref: Pick<ViewModuleRef, "moduleId" | "minVersion">,
  resolvedModuleVersions: Record<ModuleID, VersionString>,
): VersionString {
  return resolvedModuleVersions[ref.moduleId] ?? ref.minVersion;
}
